import styled from '@emotion/styled';
import css from '@styled-system/css';
import { pick } from '@styled-system/props';
import Close from 'imagesUwKluis/svg/close.svg';
import { any, bool, func } from 'prop-types';
import { useMemo } from 'react';
import ReactModal from 'react-modal';
import theme from '../../theme';
import { useMediaQuery } from '@dnhb/node_package';
import { Box, Flex } from './layouts';
import ProgressBar from './ProgressBar';

const CloseButton = styled.button({
  backgroundColor: 'inherit',
  border: 0,
  padding: 0,
  margin: 0,
  cursor: 'pointer',
});

const modalStyling = {
  boxShadow: '0px 0px 24px 0px rgba(0, 0, 0, 0.12)',
  minHeight: '128px',
  border: 0,
  padding: 0,
  overflow: 'hidden',
};

const desktopPositioning = {
  position: 'fixed',
  top: '50%',
  right: 'auto',
  bottom: 'auto',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '604px',
  maxHeight: '80vh',
};

const mobilePositioning = {
  position: 'absolute',
  top: '0',
  right: '0',
  bottom: '0',
  left: '0',
  width: 'initial',
  minWidth: 'initial',
  height: 'auto',
  borderTop: '1px solid #D3DDE0',
};

const CloseIcon = () => (
  <Close
    css={({ colors }) => ({ fill: colors.secondaryFont })}
    width="2rem"
    height="2rem"
    display="block"
  />
);

const Modal = ({
  isOpen,
  onRequestClose,
  appElement,
  children,
  showCloseButton,
  showProgressBar,
  progressBarValue,
  progressBarTotal,
  progressBarColor,
  ...boxProps
}) => {
  const isMinDesktop = useMediaQuery('(min-width: 604px)');
  const contentStyle = isMinDesktop ? desktopPositioning : mobilePositioning;

  const renderProgressBar = useMemo(
    () => (
      <ProgressBar
        value={progressBarValue}
        total={progressBarTotal}
        color={progressBarColor}
        css={{
          position: 'absolute',
          left: 0,
          bottom: 0,
          width: '100%',
        }}
      />
    ),
    [progressBarValue, progressBarTotal, progressBarColor]
  );

  return (
    <ReactModal
      appElement={appElement}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={{
        overlay: {
          zIndex: 101,
        },
        content: {
          borderRadius: isMinDesktop ? 4 : 0,
          ...css(pick(boxProps))(theme),
          ...modalStyling,
          ...contentStyle,
        },
      }}
    >
      <Box
        bg="white"
        css={{
          position: 'relative',
          boxSizing: 'border-box',
          width: '100%',
          height: '100%',
        }}
      >
        {!isMinDesktop && (
          <Flex
            position="relative"
            justifyContent="flex-end"
            alignItems="center"
            height="72px"
            p="0 16px"
            borderBottom={!showProgressBar ? 'default' : null}
          >
            {showCloseButton && (
              <CloseButton onClick={onRequestClose}>
                <CloseIcon />
              </CloseButton>
            )}
            {showProgressBar && renderProgressBar}
          </Flex>
        )}
        {isMinDesktop && showCloseButton && (
          <CloseButton
            css={{
              position: 'absolute',
              top: '24px',
              right: '24px',
            }}
            onClick={onRequestClose}
          >
            <CloseIcon />
          </CloseButton>
        )}
        <Box
          overflowY="auto"
          height={!isMinDesktop ? '100%' : null}
          maxHeight={!isMinDesktop ? 'calc(100vh - 72px)' : '80vh'}
          p={isMinDesktop ? '24px 32px' : '24px 16px'}
        >
          {children}
        </Box>
        {isMinDesktop && showProgressBar && renderProgressBar}
      </Box>
    </ReactModal>
  );
};

Modal.propTypes = {
  isOpen: bool,
  showCloseButton: bool,
  onRequestClose: func,
  appElement: any, // node veroorzaakt foutmelding
};

Modal.defaultProps = {
  isOpen: false,
  onRequestClose: () => {},
  appElement: document.body,
  showCloseButton: true,
};

export default Modal;
